import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import { CheckInInitModel, CheckInInitVariables, CheckInModel, CheckInVariables } from '../../../../models/email-template/check-in.interface';

/**
 * Mockup-styled Check-in block - a drop-in replacement for app-check-in in
 * block-renderer-v2 (same [block]/[isPreview] inputs and outputs), so it
 * covers both the center-canvas summary (isPreview=true) and the
 * right-panel edit form (isPreview=false). Reuses the real, unmodified
 * app-block-text (image upload/segment/validation), app-field-editor-v2 and
 * app-pin sub-components and the exact same CheckInVariables field names -
 * only the surrounding layout is new. Same image-left/content-right pattern
 * as Gift Voucher CTA (see gift-voucher-v2), just a smaller square image
 * column (matches the mockup's own .cr-checkin-layout, 160px vs 290px).
 */
@Component({
  selector: 'app-check-in-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './check-in-v2.component.html',
  styleUrl: './check-in-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CheckInComponentV2 extends BaseComponent implements OnInit {
  @Input() block: CheckInModel = new CheckInInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new CheckInInitVariables();
    }
  }

  imagePlaceholder = 'Image';
  imageHrefPlaceholder = 'Enter Image Redirect Link';
  imageAltPlaceholder = 'Enter Image Alt';
  headingPlaceholder = 'Enter Heading Text';
  textPlaceholder = 'Enter Text';
  ctaLinkPlaceholder = 'Enter CTA Link';
  ctaTextPlaceholder = 'Enter CTA Text';

  protected readonly ctaButtonPlaceholder = 'Insert CTA text';

  protected imageAsUpload = true;

  protected toggleImageMode(): void {
    this.imageAsUpload = !this.imageAsUpload;
  }

  get imagePreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['CheckInImageHref']);
    const segment = this.block.variables?.['CheckInImageHrefSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get ctaPreviewHref(): string {
    const base = this.helper.getFullUrl(this.block.variables?.['CheckInCtaLink']);
    const segment = this.block.variables?.['CheckInCtaLinkSegmentRegex'];
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof CheckInVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
